import {ActionCreator, createAction} from '@packages/utilities/create-action';
import type {
  AssistantCmsCollection,
  ClientFrameworkEvent,
  VersionInfo,
} from '@packages/domains/ai-assistant/data-types';
import type {
  AgentHint,
  ExternalToolResponse,
} from '@packages/systems/ai/agent/shared';
import type {
  InstantApplyRecord,
  VisualEditsMessageSummary,
} from '@packages/systems/code-libraries/designer';
import type {
  MessageRequestBody,
  ChatModel,
  DraftAttachment,
  PendingExternalToolCall,
} from './types';
import {DesignerActionFromCreator} from '@packages/systems/designer/flux';
// Re-exported rather than declared here: the deterministic visual-edits apply
// brackets its own write with these, and it cannot import the chat UI to do it
// (EXT-2020).
export {
  AI_ASSISTANT_TURN_START,
  AI_ASSISTANT_TURN_END,
  AI_ASSISTANT_INSTANT_APPLY_REVERTED,
  aiAssistantTurnStart,
  aiAssistantTurnEnd,
  aiAssistantInstantApplyReverted,
  type AiAssistantTurnMarkerPayload,
  type AiAssistantInstantApplyRevertedPayload,
} from '@packages/systems/ai/assistant-next/shared';
import {
  aiAssistantInstantApplyReverted,
  aiAssistantTurnEnd,
  aiAssistantTurnStart,
} from '@packages/systems/ai/assistant-next/shared';
import {
  ASSISTANT_OPENED,
  COMPONENT_NODE_UNFOCUSED_ALL,
  NAVIGATED_BACK_FROM_COMPONENT_EDIT,
  PAGE_SWITCHED,
  SYMBOL_NODE_UNFOCUSED,
} from '@packages/systems/designer/actions';

/**
 * Dispatch when the assistant panel is being opened.
 *
 * Shares the ASSISTANT_OPENED action type with the legacy
 * `aiAssistantOpen` creator in designer-flux, which is typed `void`. The
 * `clearConversation` field on the payload is reserved for the new
 * assistant-next store — setting it to `true` will reset conversation
 * state in the same way `aiAssistantClearConversation()` does. Do not
 * pass this flag from callsites that just open the panel.
 */
export const aiAssistantOpened: ActionCreator<
  typeof ASSISTANT_OPENED,
  {clearConversation?: boolean; clearCmsCollections?: boolean} | undefined
> = createAction(ASSISTANT_OPENED);

export const AI_ASSISTANT_SEND_MESSAGE = 'AI_ASSISTANT_SEND_MESSAGE';
export const AI_ASSISTANT_SEGMENT_STARTED = 'AI_ASSISTANT_SEGMENT_STARTED';
export const AI_ASSISTANT_AGENT_SESSION_RESOLVED =
  'AI_ASSISTANT_AGENT_SESSION_RESOLVED';
export const AI_ASSISTANT_EVENT_RECEIVED = 'AI_ASSISTANT_EVENT_RECEIVED';
export const AI_ASSISTANT_SEGMENT_ENDED = 'AI_ASSISTANT_SEGMENT_ENDED';
export const AI_ASSISTANT_CANCEL_GENERATION = 'AI_ASSISTANT_CANCEL_GENERATION';
export const AI_ASSISTANT_TOOL_HANDOFF_ABANDONED =
  'AI_ASSISTANT_TOOL_HANDOFF_ABANDONED';
export const AI_ASSISTANT_EXTERNAL_TOOL_DETECTED =
  'AI_ASSISTANT_EXTERNAL_TOOL_DETECTED';
export const AI_ASSISTANT_RESPOND_TO_EXTERNAL_TOOL =
  'AI_ASSISTANT_RESPOND_TO_EXTERNAL_TOOL';
export const AI_ASSISTANT_ERROR = 'AI_ASSISTANT_ERROR';
export const AI_ASSISTANT_CLEAR_CONVERSATION =
  'AI_ASSISTANT_CLEAR_CONVERSATION';
export const AI_ASSISTANT_HYDRATE_CONVERSATION =
  'AI_ASSISTANT_HYDRATE_CONVERSATION';
export const AI_ASSISTANT_UPDATE_VERSION_INFO =
  'AI_ASSISTANT_UPDATE_VERSION_INFO';
export const AI_ASSISTANT_SET_MODEL = 'AI_ASSISTANT_SET_MODEL';
export const AI_ASSISTANT_CODE_STREAMING_UPDATE =
  'AI_ASSISTANT_CODE_STREAMING_UPDATE';
export const AI_ASSISTANT_CODE_STREAMING_CLEAR =
  'AI_ASSISTANT_CODE_STREAMING_CLEAR';
export const AI_ASSISTANT_SET_DRAFT_MESSAGE = 'AI_ASSISTANT_SET_DRAFT_MESSAGE';
export const AI_ASSISTANT_SET_CMS_COLLECTIONS =
  'AI_ASSISTANT_SET_CMS_COLLECTIONS';
export const AI_ASSISTANT_INSTANT_APPLY_RECORDED =
  'AI_ASSISTANT_INSTANT_APPLY_RECORDED';

export const aiAssistantSendMessage: ActionCreator<
  typeof AI_ASSISTANT_SEND_MESSAGE,
  {
    conversationId: string;
    message: Omit<MessageRequestBody, 'model'>;
    /**
     * When true and a stream segment is active, cancel it before sending so
     * sidebar resubmits during VCC clarification do not queue behind HITL.
     */
    interruptActiveSegment?: boolean;
  }
> = createAction(AI_ASSISTANT_SEND_MESSAGE);

export const aiAssistantSegmentStarted: ActionCreator<
  typeof AI_ASSISTANT_SEGMENT_STARTED,
  {conversationId: string; braintrustTraceId?: string}
> = createAction(AI_ASSISTANT_SEGMENT_STARTED);

export const aiAssistantAgentSessionResolved: ActionCreator<
  typeof AI_ASSISTANT_AGENT_SESSION_RESOLVED,
  {conversationId: string; agentSessionId: string}
> = createAction(AI_ASSISTANT_AGENT_SESSION_RESOLVED);

export const aiAssistantEventReceived: ActionCreator<
  typeof AI_ASSISTANT_EVENT_RECEIVED,
  {conversationId: string; event: ClientFrameworkEvent}
> = createAction(AI_ASSISTANT_EVENT_RECEIVED);

export const aiAssistantSegmentEnded: ActionCreator<
  typeof AI_ASSISTANT_SEGMENT_ENDED,
  {conversationId: string}
> = createAction(AI_ASSISTANT_SEGMENT_ENDED);

export const aiAssistantCancelGeneration: ActionCreator<
  typeof AI_ASSISTANT_CANCEL_GENERATION,
  {conversationId: string}
> = createAction(AI_ASSISTANT_CANCEL_GENERATION);

/**
 * A local designer tool applied its change, but the transport carrying the
 * result to agent-service could not be recovered. Unlike cancel, this carries
 * no "the user stopped it" meaning: the tool succeeded and the canvas kept
 * the change.
 *
 * This only drops the undeliverable handoff — it does not end the turn. Follow
 * it with `aiAssistantError` and/or `aiAssistantSegmentEnded`, or the composer
 * stays locked.
 */
export const aiAssistantToolHandoffAbandoned: ActionCreator<
  typeof AI_ASSISTANT_TOOL_HANDOFF_ABANDONED,
  {conversationId: string}
> = createAction(AI_ASSISTANT_TOOL_HANDOFF_ABANDONED);

export const aiAssistantExternalToolDetected: ActionCreator<
  typeof AI_ASSISTANT_EXTERNAL_TOOL_DETECTED,
  {conversationId: string; toolCallEvent: PendingExternalToolCall}
> = createAction(AI_ASSISTANT_EXTERNAL_TOOL_DETECTED);

export const aiAssistantRespondToExternalTool: ActionCreator<
  typeof AI_ASSISTANT_RESPOND_TO_EXTERNAL_TOOL,
  {
    conversationId: string;
    toolCallEvent: PendingExternalToolCall;
    response: ExternalToolResponse;
    versionInfo?: VersionInfo;
    agentHint?: AgentHint;
  }
> = createAction(AI_ASSISTANT_RESPOND_TO_EXTERNAL_TOOL);

export const aiAssistantError: ActionCreator<
  typeof AI_ASSISTANT_ERROR,
  {conversationId: string; error: Error}
> = createAction(AI_ASSISTANT_ERROR);

export const aiAssistantClearConversation: ActionCreator<
  typeof AI_ASSISTANT_CLEAR_CONVERSATION,
  void
> = createAction(AI_ASSISTANT_CLEAR_CONVERSATION);

export const aiAssistantHydrateConversation: ActionCreator<
  typeof AI_ASSISTANT_HYDRATE_CONVERSATION,
  {
    conversationId: string;
    events: ClientFrameworkEvent[];
    versionInfo?: Partial<VersionInfo> | null;
    agentSessionId?: string | null;
  }
> = createAction(AI_ASSISTANT_HYDRATE_CONVERSATION);

export const aiAssistantUpdateVersionInfo: ActionCreator<
  typeof AI_ASSISTANT_UPDATE_VERSION_INFO,
  {
    conversationId: string;
    versionInfo: Partial<VersionInfo>;
  }
> = createAction(AI_ASSISTANT_UPDATE_VERSION_INFO);

export const aiAssistantSetModel: ActionCreator<
  typeof AI_ASSISTANT_SET_MODEL,
  {model: ChatModel}
> = createAction(AI_ASSISTANT_SET_MODEL);

export const aiAssistantCodeStreamingUpdate: ActionCreator<
  typeof AI_ASSISTANT_CODE_STREAMING_UPDATE,
  {code: string; componentId: string; isComplete: boolean}
> = createAction(AI_ASSISTANT_CODE_STREAMING_UPDATE);

export const aiAssistantCodeStreamingClear: ActionCreator<
  typeof AI_ASSISTANT_CODE_STREAMING_CLEAR,
  void
> = createAction(AI_ASSISTANT_CODE_STREAMING_CLEAR);

export const aiAssistantSetDraftMessage: ActionCreator<
  typeof AI_ASSISTANT_SET_DRAFT_MESSAGE,
  {draftMessage: string; draftAttachments?: DraftAttachment[]}
> = createAction(AI_ASSISTANT_SET_DRAFT_MESSAGE);

export const aiAssistantSetCmsCollections: ActionCreator<
  typeof AI_ASSISTANT_SET_CMS_COLLECTIONS,
  {cmsCollections: AssistantCmsCollection[]}
> = createAction(AI_ASSISTANT_SET_CMS_COLLECTIONS);

/**
 * Records a confirm-time instant apply that needed no assistant turn as its own
 * transcript entry (EXT-2020).
 *
 * The reducer synthesises a `user_input` event from this, so the apply renders
 * like a message that carried drafts: same group, same card, same per-turn Undo.
 * Deliberately not `AI_ASSISTANT_EVENT_RECEIVED` — that case also moves
 * `lastUserInputEventId` (the backend's credit-group id) and clears
 * `pendingMessage`, neither of which an apply should touch.
 *
 * A mixed apply records nothing here yet; part 4 puts its record on the turn's
 * own user message.
 */
export const aiAssistantInstantApplyRecorded: ActionCreator<
  typeof AI_ASSISTANT_INSTANT_APPLY_RECORDED,
  {
    /** Doubles as the synthetic event's id, so it is the group's turn id. */
    record: InstantApplyRecord;
    /** Everything the click confirmed, for the entry's card. */
    summary: VisualEditsMessageSummary | null;
  }
> = createAction(AI_ASSISTANT_INSTANT_APPLY_RECORDED);

// Foreign designer actions the reducer keys on to scope the CMS picker
// selection to the edited code component (see the reducer case).
type PageSwitchedAction = {type: typeof PAGE_SWITCHED};
type SymbolNodeUnfocusedAction = {type: typeof SYMBOL_NODE_UNFOCUSED};
type ComponentNodeUnfocusedAllAction = {
  type: typeof COMPONENT_NODE_UNFOCUSED_ALL;
};
type NavigatedBackFromComponentEditAction = {
  type: typeof NAVIGATED_BACK_FROM_COMPONENT_EDIT;
};

export type AIAssistantStoreAction =
  | DesignerActionFromCreator<typeof aiAssistantSendMessage>
  | DesignerActionFromCreator<typeof aiAssistantSegmentStarted>
  | DesignerActionFromCreator<typeof aiAssistantAgentSessionResolved>
  | DesignerActionFromCreator<typeof aiAssistantEventReceived>
  | DesignerActionFromCreator<typeof aiAssistantSegmentEnded>
  | DesignerActionFromCreator<typeof aiAssistantCancelGeneration>
  | DesignerActionFromCreator<typeof aiAssistantToolHandoffAbandoned>
  | DesignerActionFromCreator<typeof aiAssistantExternalToolDetected>
  | DesignerActionFromCreator<typeof aiAssistantRespondToExternalTool>
  | DesignerActionFromCreator<typeof aiAssistantError>
  | DesignerActionFromCreator<typeof aiAssistantClearConversation>
  | DesignerActionFromCreator<typeof aiAssistantHydrateConversation>
  | DesignerActionFromCreator<typeof aiAssistantUpdateVersionInfo>
  | DesignerActionFromCreator<typeof aiAssistantSetModel>
  | DesignerActionFromCreator<typeof aiAssistantCodeStreamingUpdate>
  | DesignerActionFromCreator<typeof aiAssistantCodeStreamingClear>
  | DesignerActionFromCreator<typeof aiAssistantSetDraftMessage>
  | DesignerActionFromCreator<typeof aiAssistantSetCmsCollections>
  | DesignerActionFromCreator<typeof aiAssistantInstantApplyRecorded>
  | DesignerActionFromCreator<typeof aiAssistantInstantApplyReverted>
  | DesignerActionFromCreator<typeof aiAssistantTurnStart>
  | DesignerActionFromCreator<typeof aiAssistantTurnEnd>
  | DesignerActionFromCreator<typeof aiAssistantOpened>
  | PageSwitchedAction
  | SymbolNodeUnfocusedAction
  | ComponentNodeUnfocusedAllAction
  | NavigatedBackFromComponentEditAction;
